/**
 * File Upload Component.
 *
 * Drag-and-drop or click-to-browse file upload with preview of uploaded files.
 */

import { useRef } from "react";
import { Upload, X, FileIcon } from "lucide-react";
import {
  useAttachments,
  useUploadAttachment,
  useDeleteAttachment,
} from "@/features/attachments/api";

interface FileUploadProps {
  entityType: string;
  entityId: string;
}

function FileUpload({ entityType, entityId }: FileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: attachments, isLoading } = useAttachments(entityType, entityId);
  const uploadMutation = useUploadAttachment();
  const deleteMutation = useDeleteAttachment();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      await uploadMutation.mutateAsync({ entityType, entityId, file });
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-3">
      {/* Upload Area */}
      <div
        onClick={() => fileInputRef.current?.click()}
        className="flex cursor-pointer flex-col items-center gap-2 rounded-[var(--radius-md)] border-2 border-dashed border-[var(--border-default)] p-6 transition-colors hover:border-[var(--border-focus)] hover:bg-[var(--fill-info)]"
      >
        <Upload className="h-8 w-8 text-[var(--text-tertiary)]" />
        <p className="text-sm text-[var(--text-secondary)]">
          {uploadMutation.isPending ? "Uploading..." : "Click to upload files"}
        </p>
        <p className="text-xs text-[var(--text-tertiary)]">
          Images, documents, PDFs
        </p>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Attachments List */}
      {isLoading ? (
        <p className="text-sm text-[var(--text-tertiary)]">Loading...</p>
      ) : attachments && attachments.length > 0 ? (
        <div className="space-y-2">
          {attachments.map((att) => (
            <div
              key={att.id}
              className="flex items-center justify-between rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2"
            >
              <div className="flex items-center gap-2">
                <FileIcon className="h-4 w-4 text-[var(--text-tertiary)]" />
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)]">
                    {att.file_name}
                  </p>
                  <p className="text-xs text-[var(--text-tertiary)]">
                    {formatSize(att.file_size)} &middot;{" "}
                    {new Date(att.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <button
                onClick={() =>
                  deleteMutation.mutate({ id: att.id, entityType, entityId })
                }
                className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default FileUpload;
